import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaceIcon from '@mui/icons-material/Place';
import SchoolIcon from '@mui/icons-material/School';
import { useTranslation } from 'react-i18next';

import { useGradeLabel } from '../../hooks/useGradeLabel';
import { gradeTranslateAttr } from '../../lib/gradeLabel';
import { RecentSearch } from '../../lib/recentSearches';
import { GradeOption } from '../../types/finderConfig';

interface RecentSearchItemProps {
  search: RecentSearch;
  orderedGrades: GradeOption[];
  onResume: (s: RecentSearch) => void;
  onChangeGrade: (s: RecentSearch, grade: string) => void;
  onRemove: (id: string) => void;
}

/** One recent-search row: resume on click, change grade, or remove. */
const RecentSearchItem = ({
  search, orderedGrades, onResume, onChangeGrade, onRemove,
}: RecentSearchItemProps) => {
  const { t } = useTranslation();
  const getGradeLabel = useGradeLabel();
  const gradeValue = orderedGrades.some((g) => g.value === search.grade) ? search.grade : '';

  return (
    <Box
      component="li"
      sx={{
        border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5,
      }}
    >
      <ButtonBase
        onClick={() => onResume(search)}
        sx={{
          width: '100%',
          borderRadius: 1,
          p: 0.5,
          '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
          <PlaceIcon fontSize="small" color="secondary" sx={{ flexShrink: 0 }} />
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              fontSize: '0.875rem',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {search.label || t('finder.recent_searches_pinned_location')}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{ fontWeight: 600, flexShrink: 0 }}
          >
            {t('finder.recent_searches_resume')}
          </Typography>
        </Stack>
      </ButtonBase>

      <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
        <SchoolIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
        <TextField
          select
          size="small"
          value={gradeValue}
          onChange={(e) => {
            const next = e.target.value;
            if (next && next !== search.grade) onChangeGrade(search, next);
          }}
          aria-label={t('finder.recent_searches_change_grade')}
          sx={{ flex: 1 }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              const selected = orderedGrades.find((g) => g.value === value);
              const label = selected
                ? getGradeLabel(selected)
                : { text: search.grade, allowGoogleTranslate: true };
              return (
                <Box component="span" translate={gradeTranslateAttr(label)}>
                  {label.text}
                </Box>
              );
            },
          }}
        >
          {orderedGrades.map((g) => {
            const label = getGradeLabel(g);
            return (
              <MenuItem key={g.value} value={g.value} translate={gradeTranslateAttr(label)}>
                {label.text}
              </MenuItem>
            );
          })}
        </TextField>
        <IconButton
          size="small"
          aria-label={t('finder.recent_searches_remove')}
          onClick={() => onRemove(search.id)}
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default RecentSearchItem;
